import React from 'react';
import { Button, Input, InputNumber, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import VariantPicker from '../components/inventory/VariantPicker';
import { formatCurrency } from '../utils/currency';
import { formatUnit } from '../utils/units';
import { formatConversionValue } from '../utils/dimensionConversion';
import { formatProductDimensions } from '../utils/productDimensions';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../utils/polishNumberInput';
import { PZ_QUANTITY_MAX } from '../utils/warehouseQuantity';
import { sanitizeProductDisplayName } from '../utils/productSelectorLabel';
import type { PZItem, ProductVariant } from '../types/pz';
import WarehouseQuantityField from '../modules/warehouse/WarehouseQuantityField';
import {
  WAREHOUSE_CONVERTED_QUANTITY_MAX,
  WarehouseQuantityPatch,
  formatWarehouseQuantity,
  getWarehouseLineUnit,
} from '../modules/warehouse/warehouseQuantityConversion';
import './PZItemsSection.css';

export interface PZItemRow extends Partial<PZItem> {
  key?: string;
}

interface PZItemsSectionProps {
  items: PZItemRow[];
  isEditMode: boolean;
  canViewPurchasePrices: boolean;
  canEditPurchasePrices: boolean;
  /** Rendered as-is; PZDetail owns the derivation (formatWarehouseQuantityTotals). */
  quantityTotalsLabel: string;
  totalValue: { amount: number; currency: string };
  onItemChange: (key: string, field: keyof PZItemRow, value: unknown) => void;
  /** Quantity and conversionUnit are chosen together in the conversion modal. */
  onQuantityChange: (key: string, patch: WarehouseQuantityPatch) => void;
  onVariantSelect: (key: string, variantId: string | undefined, variant?: ProductVariant) => void;
  onRemoveItem: (key: string) => void;
}

const formatLineDimensions = (item: PZItemRow) => formatProductDimensions({
  width: item.width,
  height: item.height,
  length: item.length,
  thickness: item.thickness,
  productCategoryDimensionDisplayProfile:
    item.productCategoryDimensionDisplayProfile ?? 'GENERIC',
}) || '-';

const PZItemsSection: React.FC<PZItemsSectionProps> = ({
  items,
  isEditMode,
  canViewPurchasePrices,
  canEditPurchasePrices,
  quantityTotalsLabel,
  totalValue,
  onItemChange,
  onQuantityChange,
  onVariantSelect,
  onRemoveItem,
}) => {
  const { t, i18n } = useTranslation(['pz', 'common']);

  if (items.length === 0) {
    return <div className="pz-items-empty">{t('pz:noItems')}</div>;
  }

  return (
    <div className="pz-items-section" data-testid="pz-items-card-layout">
      <div className="pz-items-list">
        {items.map((item, index) => {
          const itemKey = item.key || String(index);
          // The receipt quantity is denominated in the product's base unit, so the
          // conversion must start from that unit — otherwise an m2-based line has
          // its area multiplied by the board area a second time.
          const conversionLabel = formatConversionValue(
            item.conversionUnit,
            { ...item, unit: getWarehouseLineUnit({ unit: item.unit }) },
            item.quantityOrdered || 0,
          );
          const pricingQuantity = item.purchaseQuantity ?? item.quantityOrdered ?? 0;
          const pricingUnit = item.purchaseUnit || item.unit;
          const lineTotal = pricingQuantity * (item.unitPrice || 0);
          const productLabel = sanitizeProductDisplayName(
            item.productName || item.productVariant?.product?.name,
            [
              item.productCode,
              item.sku,
              item.ean,
              item.productVariantId,
              item.productVariant?.id,
              item.productVariant?.sku,
              item.productVariant?.product?.id,
              item.productVariant?.product?.code,
              item.productVariant?.product?.ean,
            ],
          );

          return (
            <article className="pz-item-card" key={itemKey} data-testid="pz-item-card">
              <div className="pz-item-card__header">
                <div className="pz-item-card__number">
                  {t('pz:itemFields.position', { number: index + 1 })}
                </div>
                {isEditMode && (
                  <Button
                    className="pz-item-card__remove"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onRemoveItem(itemKey)}
                    disabled={items.length === 1 || Boolean(item.sourceSupplierOrderItemId)}
                    aria-label={t('common:delete')}
                  >
                    {t('common:delete')}
                  </Button>
                )}
              </div>

              <div className="pz-item-card__product">
                <Typography.Text className="pz-item-field__label">
                  {t('pz:itemFields.productVariant')}
                </Typography.Text>
                {isEditMode ? (
                  <VariantPicker
                    allowCreate
                    purchasePriceScope="warehouse-document"
                    value={item.productVariantId || undefined}
                    disabled={Boolean(item.sourceSupplierOrderItemId)}
                    onChange={(variantId, variant) => onVariantSelect(itemKey, variantId, variant)}
                    placeholder={t('pz:actions.selectVariant')}
                    style={{ width: '100%' }}
                  />
                ) : (
                  <Typography.Text className="pz-item-card__product-name">
                    {productLabel || '-'}
                  </Typography.Text>
                )}
              </div>

              <div className="pz-item-card__details">
                <div className="pz-item-field pz-item-field--ean">
                  <Typography.Text className="pz-item-field__label">
                    {t('pz:itemFields.ean')}
                  </Typography.Text>
                  {isEditMode ? (
                    <Input
                      aria-label={t('pz:itemFields.ean')}
                      value={item.ean}
                      maxLength={64}
                      placeholder={t('pz:itemFields.ean')}
                      onChange={(event) => onItemChange(itemKey, 'ean', event.target.value)}
                    />
                  ) : (
                    <Typography.Text>{item.ean || '-'}</Typography.Text>
                  )}
                </div>

                <div className="pz-item-field pz-item-field--dimensions">
                  <Typography.Text className="pz-item-field__label">
                    {t('pz:itemFields.dimensionsMm')}
                  </Typography.Text>
                  <Typography.Text>{formatLineDimensions(item)}</Typography.Text>
                </div>

                <div className="pz-item-field pz-item-field--quantity">
                  <Typography.Text className="pz-item-field__label">
                    {t('pz:itemFields.quantityOrdered')}
                  </Typography.Text>
                  {isEditMode ? (
                    <WarehouseQuantityField
                      line={{
                        ...item,
                        quantity: item.quantityOrdered,
                      }}
                      onChange={(patch) => onQuantityChange(itemKey, patch)}
                      pricingQuantity={item.purchaseQuantity}
                      pricingUnit={item.purchaseUnit}
                      physicalQuantityLocked={Boolean(item.sourceSupplierOrderItemId)}
                      maxQuantity={PZ_QUANTITY_MAX}
                      maxConvertedQuantity={WAREHOUSE_CONVERTED_QUANTITY_MAX}
                      noDimensionsLabel={t('pz:conversion.noDimensions')}
                      unitLabel={t('pz:itemFields.unit')}
                    />
                  ) : (
                    <Typography.Text strong>
                      {formatWarehouseQuantity(item.quantityOrdered, item.unit, i18n.language)}
                    </Typography.Text>
                  )}
                </div>

                {!isEditMode && (
                  <div className="pz-item-field pz-item-field--conversion">
                    <Typography.Text className="pz-item-field__label">
                      {t('pz:itemFields.conversion')}
                    </Typography.Text>
                    <Typography.Text>
                      {item.conversionUnit
                        ? (conversionLabel || t('pz:conversion.noDimensions'))
                        : '-'}
                    </Typography.Text>
                  </div>
                )}

                {canViewPurchasePrices && (
                  <>
                    <div className="pz-item-field pz-item-field--unit-price">
                      <Typography.Text className="pz-item-field__label">
                        {t('pz:itemFields.unitPrice')}
                      </Typography.Text>
                      {isEditMode && canEditPurchasePrices ? (
                        <InputNumber
                          aria-label={t('pz:itemFields.unitPrice')}
                          min={0}
                          step={0.01}
                          precision={4}
                          value={item.unitPrice}
                          onChange={(value) => onItemChange(itemKey, 'unitPrice', value)}
                          // Price follows the persisted purchase pair, not the
                          // stock-movement unit. The suffix makes the supplier
                          // invoice basis explicit.
                          addonAfter={`/ ${formatUnit(pricingUnit, i18n.language)}`}
                          style={{ width: '100%' }}
                          {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                        />
                      ) : (
                        <Typography.Text>
                          {item.unitPrice === undefined
                            ? '-'
                            : `${formatCurrency(item.unitPrice, item.currency || 'PLN')} / ${formatUnit(pricingUnit, i18n.language)}`}
                        </Typography.Text>
                      )}
                    </div>

                    <div className="pz-item-field pz-item-field--total-price">
                      <Typography.Text className="pz-item-field__label">
                        {t('pz:itemFields.totalPrice')}
                      </Typography.Text>
                      <Typography.Text strong>
                        {formatCurrency(lineTotal, item.currency || 'PLN')}
                      </Typography.Text>
                    </div>
                  </>
                )}

                <div className="pz-item-field pz-item-field--notes">
                  <Typography.Text className="pz-item-field__label">
                    {t('pz:itemFields.notes')}
                  </Typography.Text>
                  {isEditMode ? (
                    <Input
                      aria-label={t('pz:itemFields.notes')}
                      value={item.notes}
                      onChange={(event) => onItemChange(itemKey, 'notes', event.target.value)}
                      placeholder={t('pz:itemFields.notes')}
                    />
                  ) : (
                    <Typography.Text>{item.notes}</Typography.Text>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <div className="pz-items-summary" data-testid="pz-items-summary">
        <Typography.Text strong>{t('common:total')}</Typography.Text>
        <div className="pz-items-summary__values">
          <Typography.Text>
            {t('pz:fields.totalQuantity')}: <strong>{quantityTotalsLabel || '-'}</strong>
          </Typography.Text>
          {canViewPurchasePrices && (
            <Typography.Text>
              {t('pz:fields.totalValue')}: <strong>{formatCurrency(totalValue.amount, totalValue.currency)}</strong>
            </Typography.Text>
          )}
        </div>
      </div>
    </div>
  );
};

export default PZItemsSection;
